// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/**
 * Colour vocabulary, used identically across every view so an entity keeps its
 * colour everywhere. Two axes:
 *   - Position: did a facility emit above (OVER) or below (UNDER) its baseline.
 *   - Sector: an industry's categorical colour.
 */

/** Emitted above the baseline — ember. Emitted below — teal. */
export const OVER = '#e2562b';
export const OVER_SOFT = '#f4b8a3';
export const UNDER = '#0d9488';
export const UNDER_SOFT = '#9fd8d1';
export const NEUTRAL = '#94a3b8';
export const INK = '#334155';

/** Colour for a facility's position (excess = covered - baseline). */
export function positionColour(excess: number): string {
  if (excess > 0.5) return OVER;
  if (excess < -0.5) return UNDER;
  return NEUTRAL;
}
export function positionSoft(excess: number): string {
  if (excess > 0.5) return OVER_SOFT;
  if (excess < -0.5) return UNDER_SOFT;
  return '#e2e8f0';
}

/** Curated sector colours for the industries that actually appear. */
export const SECTOR_COLOURS: Record<string, string> = {
  'Coal mining': '#1f2937',
  'Metal ore mining': '#b45309',
  'Oil and gas extraction': '#d97706',
  'Gas supply': '#f59e0b',
  'Basic non-ferrous metal manufacturing': '#6366f1',
  'Basic ferrous metal manufacturing': '#0ea5e9',
  'Basic ferrous product manufacturing': '#0ea5e9',
  'Cement, lime, plaster and concrete product manufacturing': '#a8a29e',
  'Electricity generation': '#eab308',
  'Basic chemical manufacturing': '#8b5cf6',
  'Other basic chemical product manufacturing': '#8b5cf6',
  'Fertiliser and pesticide manufacturing': '#a855f7',
  'Petroleum and coal product manufacturing': '#78716c',
  'Pulp, paper and paperboard manufacturing': '#65a30d',
  'Rail freight transport': '#0d9488',
  'Road freight transport': '#14b8a6',
  'Air and space transport': '#2563eb',
  'Water freight transport': '#0891b2',
  'Pipeline and other transport': '#059669',
  'Waste treatment, disposal and remediation services': '#84cc16',
};

const FALLBACK = ['#64748b', '#9333ea', '#db2777', '#0891b2', '#ca8a04', '#4f46e5', '#be123c'];

/** A stable colour for any sector label (curated first, else hashed fallback). */
export function sectorColour(sector: string): string {
  if (SECTOR_COLOURS[sector]) return SECTOR_COLOURS[sector];
  let h = 0;
  for (let i = 0; i < sector.length; i++) h = (h * 31 + sector.charCodeAt(i)) >>> 0;
  return FALLBACK[h % FALLBACK.length];
}

export const STATE_NAMES: Record<string, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'Australian Capital Territory',
  MULTI: 'National / multi-state',
};
export const STATE_ORDER = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
